// Sharing a capability between the roles of one team.
//
// A capability imported from another role of the same team is the same
// capability on both: one id, one name, one set of levels, one grading, so a
// person's level in it follows them between those roles. Roles of different
// teams never share one. A role that changes a shared capability's levels has,
// in effect, used it as a starting point: what it has now is a different
// capability, its own, and must be given a name of its own. Sent with the
// shared capability's id and the new name, the backend forks it (a new id,
// forkedFrom the shared one) and its people carry their level over; the other
// roles keep the one they shared, as it was. A level change under the shared
// name is refused (409). Renaming it alone forks it the same way: another name
// is another capability.
//
// No React here, so it can be tested on its own (sharedCapabilities.test.ts).

import { LEVEL_ORDER } from '../Program/components/learning-lifecycle/teamCapability';
import {
  MAX_CAPABILITIES,
  MAX_NAME,
  type LevelCriteria,
  type SetupCapability,
  type SetupRole
} from './capabilitySetup';

const tidy = (text: string | undefined) => (text ?? '').trim().replace(/\s+/g, ' ');

/** Two names that read the same, whatever their case and spacing. */
export const sameName = (a: string, b: string) => tidy(a).toLowerCase() === tidy(b).toLowerCase();

/** The same four levels, word for word. */
export const sameLevels = (a: LevelCriteria, b: LevelCriteria) =>
  LEVEL_ORDER.every((level) => tidy(a[level]) === tidy(b[level]));

/** One capability, as two roles of a team hold it: the same id. */
export const isSameCapability = (a: SetupCapability, b: SetupCapability) => a.id === b.id;

const teamMates = (roles: SetupRole[], role: SetupRole) =>
  roles.filter((r) => r.id !== role.id && r.teamId === role.teamId);

/** The other roles of this role's team that share the capability. */
export const sharedWith = (roles: SetupRole[], role: SetupRole, capability: SetupCapability): SetupRole[] =>
  teamMates(roles, role).filter((r) => r.capabilities.some((c) => isSameCapability(c, capability)));

/** A shared capability whose levels an edit to one role changes: as it was, as it now reads, and the roles that share it. */
export interface SharedEdit {
  before: SetupCapability;
  after: SetupCapability;
  roles: SetupRole[];
}

/**
 * The shared capabilities whose levels a role's unsaved edits change while they
 * still carry the shared name: each must be renamed before it can be saved,
 * since it is no longer the capability the other roles hold. `saved` is the
 * role as last saved and `roles` every role as last saved.
 *
 * Not among them: one already renamed in the same edit (it has its own name),
 * and one taken off the role (the other roles keep it as it is).
 */
export const sharedLevelEdits = (roles: SetupRole[], saved: SetupRole, draft: SetupRole): SharedEdit[] =>
  saved.capabilities.flatMap((before) => {
    const after = draft.capabilities.find((c) => c.id === before.id);
    if (!after || sameLevels(before.criteria, after.criteria) || !sameName(before.name, after.name)) return [];
    const others = sharedWith(roles, saved, before);
    return others.length > 0 ? [{ before, after, roles: others }] : [];
  });

/** Why this name can't make the capability the role's own, or null when it can. */
export const ownNameProblem = (roles: SetupRole[], draft: SetupRole, edit: SharedEdit, name: string): string | null => {
  const wanted = tidy(name);
  if (!wanted) return 'Give it a name.';
  if (wanted.length > MAX_NAME) return `A name can be at most ${MAX_NAME} characters.`;
  if (sameName(wanted, edit.before.name)) {
    return `Choose a different name, so this capability is not confused with the ${tidy(
      edit.before.name
    )} the other roles keep.`;
  }
  if (draft.capabilities.some((c) => c.id !== edit.after.id && sameName(c.name, wanted))) {
    return `${draft.name} already has a capability called ${wanted}.`;
  }
  const holder = teamMates(roles, draft).find((r) => r.capabilities.some((c) => sameName(c.name, wanted)));
  return holder ? `${holder.name} already has a capability called ${wanted}.` : null;
};

/** The draft with each capability that became the role's own carrying the name given to it (by its id). */
export const withOwnNames = (draft: SetupRole, names: Record<string, string | undefined>): SetupRole => ({
  ...draft,
  capabilities: draft.capabilities.map((c) => {
    const name = names[c.id];
    return name === undefined ? c : { ...c, name: tidy(name).slice(0, MAX_NAME) };
  })
});

/**
 * Why a capability's name can't be saved beside the team's other roles, or null
 * when it can: a name given here (by a rename, or to a new capability) that
 * another role of the team already uses for another capability. Within a team
 * a name means one capability; to have that one, import it. (A new capability
 * with a team-mate's name and its four levels is that capability, imported:
 * the backend shares it.)
 */
export const teamNameProblem = (
  roles: SetupRole[],
  saved: SetupRole,
  draft: SetupRole,
  capabilityId: string
): string | null => {
  const capability = draft.capabilities.find((c) => c.id === capabilityId);
  if (!capability || !tidy(capability.name)) return null;
  // Its name as saved was not given here: nothing that was fine before is refused now.
  const before = saved.capabilities.find((c) => c.id === capabilityId);
  if (before && sameName(before.name, capability.name)) return null;
  const clashes = (c: SetupCapability) =>
    c.id !== capability.id &&
    sameName(c.name, capability.name) &&
    !(!before && sameLevels(c.criteria, capability.criteria));
  const holder = teamMates(roles, draft).find((r) => r.capabilities.some(clashes));
  return holder
    ? `${holder.name} already has a capability called ${tidy(
        capability.name
      )}. Import that one to share it, or give this one another name.`
    : null;
};

/**
 * The team-mate's capability that a capability added here clashes with (the
 * one teamNameProblem names): the same name, with levels of its own. The new
 * one can be turned into an import of it (withImported), and the two roles
 * then share it. Null for a capability already saved: renaming one to a
 * team-mate's name is refused, not turned into that capability.
 */
export const clashToImport = (
  roles: SetupRole[],
  saved: SetupRole,
  draft: SetupRole,
  capabilityId: string
): { role: SetupRole; capability: SetupCapability } | null => {
  const capability = draft.capabilities.find((c) => c.id === capabilityId);
  if (!capability || !tidy(capability.name) || saved.capabilities.some((c) => c.id === capabilityId)) return null;
  const clashes = (c: SetupCapability) =>
    sameName(c.name, capability.name) && !sameLevels(c.criteria, capability.criteria);
  const holder = teamMates(roles, draft).find((r) => r.capabilities.some(clashes));
  const theirs = holder?.capabilities.find(clashes);
  return holder && theirs ? { role: holder, capability: theirs } : null;
};

/** The draft with one capability made an import of a team-mate's: its id, name and four levels, so the two share it. */
export const withImported = (draft: SetupRole, capabilityId: string, from: SetupCapability): SetupRole => ({
  ...draft,
  capabilities: draft.capabilities.map((c) =>
    c.id === capabilityId ? { ...c, id: from.id, name: from.name, criteria: { ...from.criteria } } : c
  )
});

/** The most capabilities Elliott suggests in one go (the backend's own limit). */
const SUGGESTED_AT_ONCE = 6;

/** The names, tidied, each once whatever its case, blanks left out. */
const uniqueNames = (names: string[]) =>
  names.map(tidy).filter((name, i, all) => name && all.findIndex((n) => sameName(n, name)) === i);

/**
 * The names Elliott is told are taken when it suggests capabilities for a
 * role: the role's own, then the other names its team uses. Within a team a
 * name means one capability, so a team-mate's name could only be imported,
 * never saved as this role's own. The backend counts the whole list as the
 * role's own when it works out how many more the role has room for, so the
 * team's names stop where they would cut into a full set of suggestions.
 */
export const suggestionNamesTaken = (roles: SetupRole[], role: SetupRole): string[] => {
  const own = uniqueNames(role.capabilities.map((c) => c.name));
  const team = uniqueNames(teamMates(roles, role).flatMap((r) => r.capabilities.map((c) => c.name))).filter(
    (name) => !own.some((o) => sameName(o, name))
  );
  return [...own, ...team.slice(0, Math.max(0, MAX_CAPABILITIES - SUGGESTED_AT_ONCE - own.length))];
};

/** The capabilities a role can import: those of its team's other roles, each once, that it does not hold yet. */
export const importable = (
  roles: SetupRole[],
  role: SetupRole
): { role: SetupRole; capabilities: { capability: SetupCapability; held: boolean }[] }[] =>
  teamMates(roles, role)
    .filter((r) => r.capabilities.length > 0)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((r) => ({
      role: r,
      capabilities: r.capabilities.map((capability) => ({
        capability,
        // On the role already (shared), or a name already on it: it has its own of that name.
        held: role.capabilities.some((c) => c.id === capability.id || sameName(c.name, capability.name))
      }))
    }));
